import { Card, CardContent } from "@/components/ui/card";
import { ReportMetricCard } from './ReportMetricCard';
import { Receipt } from 'lucide-react';
import { Expense } from '../../../services/supabase';
import { useExpenses } from '../../../context/ExpensesContext';

interface ExpensesHeaderProps {
  expenses: Expense[];
  onAddExpense: () => void;
}

export const ExpensesHeader = ({ expenses, onAddExpense }: ExpensesHeaderProps) => {
  const { averageNonFlightExpense } = useExpenses();

  // Calculate statistics
  const plannedExpenses = expenses.filter(e => e.is_planned).reduce((sum, e) => sum + e.amount, 0);

  // Get date range
  const dates = expenses.map(e => new Date(e.expense_date));
  const minDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
  const maxDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <ReportMetricCard
        value={expenses.length}
        label="Total Expenses"
        sublabel={plannedExpenses > 0 ? `${expenses.filter(e => e.is_planned).length} planned` : undefined}
      />

      <Card>
        <CardContent className="flex flex-col justify-between p-0 h-full min-h-[200px]">
          <div className="flex items-center justify-center flex-1">
            <div className="text-5xl font-bold tracking-tight">
              ${averageNonFlightExpense.toFixed(2)}
            </div>
          </div>
          <div className="text-center p-4 bg-muted/30">
            <div className="text-sm text-muted-foreground font-medium">
              Average Expense
            </div>
            <div className="text-xs text-muted-foreground mt-1">Excluding flights</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col justify-between p-0 h-full min-h-[200px]">
          <div className="flex items-center justify-center flex-1">
            <div className="text-3xl font-bold tracking-tight">{formatDate(minDate)}</div>
          </div>
          <div className="text-center p-4 bg-muted/30">
            <div className="text-sm text-muted-foreground font-medium">
              Date Range
            </div>
            <div className="text-xs text-muted-foreground mt-1">to {formatDate(maxDate)}</div>
          </div>
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:bg-primary/5 hover:border-primary/50 transition-all group"
        onClick={onAddExpense}
      >
        <CardContent className="flex flex-col items-center justify-center py-12 px-4">
          <div className="flex flex-col items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <Receipt className="h-8 w-8 text-primary" />
            </div>
            <div className="text-xl font-semibold text-center">Add more</div>
            <div className="text-xs text-muted-foreground text-center">Click to add expense</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
